import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { isAxiosError } from 'axios';
import {
  cancelSubscription,
  createCheckoutSession,
  fetchSubscription,
  fetchSubscriptionPlans,
  openBillingPortal,
  resumeSubscription,
} from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import type { Plan, Subscription } from '../types';

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function BillingPage(): JSX.Element {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: subscription } = useQuery<Subscription | undefined>({
    queryKey: ['subscription', user?.organizationId],
    queryFn: async () => {
      if (!user) return undefined;
      try {
        return await fetchSubscription(user.organizationId);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return undefined;
        }
        throw error;
      }
    },
    enabled: Boolean(user?.organizationId),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const { data: plans = [] } = useQuery<Plan[]>({
    queryKey: ['subscription-plans'],
    queryFn: fetchSubscriptionPlans,
    enabled: Boolean(user),
    staleTime: 1000 * 60 * 60,
  });

  const checkoutMutation = useMutation({
    mutationFn: async (priceId: string) => {
      if (!user) throw new Error('Not authenticated');
      const cancelUrl = `${window.location.origin}/app/billing`;
      const url = await createCheckoutSession(user.organizationId, { priceId, cancelUrl });
      window.location.href = url;
    },
  });

  const portalMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      const url = await openBillingPortal(user.organizationId, { returnUrl: `${window.location.origin}/app/billing` });
      window.location.href = url;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      await cancelSubscription(user.organizationId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['subscription', user?.organizationId] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      await resumeSubscription(user.organizationId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['subscription', user?.organizationId] });
    },
  });

  const currentPlanSlug = subscription?.plan;
  const plansWithState = useMemo(
    () =>
      plans.map((plan) => ({
        ...plan,
        isCurrent: plan.slug === currentPlanSlug,
      })),
    [plans, currentPlanSlug]
  );

  const busy =
    checkoutMutation.isPending ||
    portalMutation.isPending ||
    cancelMutation.isPending ||
    resumeMutation.isPending;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="space-y-6">
      <header className="glass-card p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Billing</p>
        <h1 className="mt-1 text-2xl font-semibold text-foreground">Manage your InspectAI subscription</h1>
        <p className="text-sm text-muted-foreground">
          Upgrade plans, access invoices, or adjust renewal preferences directly from this workspace.
        </p>
      </header>

      <section className="glass-card space-y-6 p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Plan overview</h2>
            <p className="text-sm text-muted-foreground">Current plan status and usage snapshot.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn-glass btn-secondary focus-ring text-sm font-semibold disabled:opacity-60"
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending || !subscription}
            >
              {portalMutation.isPending ? 'Opening portal…' : 'Open billing portal'}
            </button>
            {subscription?.status !== 'canceled' ? (
              <button
                type="button"
                className="btn-glass btn-secondary focus-ring text-sm font-semibold disabled:opacity-60"
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending || busy}
              >
                {cancelMutation.isPending ? 'Canceling…' : 'Cancel at period end'}
              </button>
            ) : (
              <button
                type="button"
                className="btn-glass btn-primary focus-ring text-sm font-semibold disabled:opacity-60"
                onClick={() => resumeMutation.mutate()}
                disabled={resumeMutation.isPending || busy}
              >
                {resumeMutation.isPending ? 'Resuming…' : 'Resume subscription'}
              </button>
            )}
          </div>
        </div>
        {subscription ? (
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="glass-card p-5 text-sm">
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Plan</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{subscription.plan}</p>
              <p className="text-xs text-muted-foreground">Status: {subscription.status}</p>
            </div>
            <div className="glass-card p-5 text-sm">
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Usage</p>
              <p className="mt-2 text-lg font-semibold text-foreground">
                {(subscription.usage.length > 0 ? subscription.usage[subscription.usage.length - 1].totalReports : 0)} / {subscription.reportLimit} reports
              </p>
              <p className="text-xs text-muted-foreground">Seats: {subscription.seats}</p>
            </div>
            <div className="glass-card p-5 text-sm">
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Stripe IDs</p>
              <p className="text-xs text-muted-foreground">Customer: {subscription.stripeCustomerId ?? '—'}</p>
              <p className="text-xs text-muted-foreground">Subscription: {subscription.stripeSubscriptionId ?? '—'}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Subscription data unavailable. Start a plan to unlock InspectAI premium features.</p>
        )}
      </section>

      <section className="glass-card space-y-6 p-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Available plans</h2>
          <p className="text-sm text-muted-foreground">Select a plan to launch Stripe checkout. Billing is managed securely by Stripe.</p>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          {plansWithState.map((plan) => {
            const priceLabel =
              plan.priceMonthly != null ? `${currencyFormatter.format(plan.priceMonthly)} / mo` : 'Custom pricing';
            const isCurrent = plan.isCurrent;
            const hasCheckoutPrice = Boolean(plan.stripePriceId);
            const isProPlan = plan.slug === 'pro';
            const isCustomPlan = plan.isCustom || plan.priceMonthly == null;

            let buttonLabel = 'Select plan';
            if (isCurrent) {
              buttonLabel = 'Current plan';
            } else if (isCustomPlan) {
              buttonLabel = 'Contact support';
            } else if (!hasCheckoutPrice) {
              buttonLabel = 'Configure Stripe billing';
            } else if (isProPlan) {
              buttonLabel = 'Upgrade to Pro';
            } else if (checkoutMutation.isPending) {
              buttonLabel = 'Redirecting…';
            }

            const buttonDisabled =
              isCurrent || checkoutMutation.isPending || busy || (!hasCheckoutPrice && !isCustomPlan);

            return (
              <article
                key={plan.id}
                className={clsx(
                  'space-y-5 rounded-2xl border border-white/20 p-6 shadow-lg transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10',
                  isCurrent ? 'bg-primary/10' : 'bg-white/40 dark:bg-white/10'
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  </div>
                  <p className="text-lg font-semibold text-foreground">{priceLabel}</p>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {plan.features.map((feature) => (
                    <li key={feature}>• {feature}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={clsx(
                    'btn-glass focus-ring text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60',
                    isCurrent || (!hasCheckoutPrice && !isCustomPlan) ? 'btn-secondary' : 'btn-primary'
                  )}
                  disabled={buttonDisabled}
                  onClick={() => {
                    if (isCurrent || checkoutMutation.isPending || busy) {
                      return;
                    }
                    if (isCustomPlan) {
                      window.open('mailto:raykundan57@gmail.com?subject=InspectAI%20custom%20plan', '_blank', 'noreferrer');
                      return;
                    }
                    if (!hasCheckoutPrice || !plan.stripePriceId) {
                      return;
                    }
                    checkoutMutation.mutate(plan.stripePriceId);
                  }}
                >
                  {buttonLabel}
                </button>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
